import { useState } from "react";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { RoleWithPermissions } from "@forgepoint/shared";
import { useRolesStore } from "@/store/rolesStore";

interface RoleListProps {
  /** The roles to render, defaults first */
  roles: RoleWithPermissions[];
  /** Called when the user clicks edit on a custom role */
  onEdit: (role: RoleWithPermissions) => void;
}

/**
 * List of the organization's roles. Default roles render read-only; custom
 * roles get edit and delete actions with a delete confirmation dialog.
 * @param props - RoleListProps
 * @returns Role list element
 */
export function RoleList({ roles, onEdit }: RoleListProps) {
  const loadingById = useRolesStore((state) => state.loadingById);
  const deleteRole = useRolesStore((state) => state.deleteRole);

  const [roleToDelete, setRoleToDelete] = useState<RoleWithPermissions | null>(
    null,
  );

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) {
      return;
    }
    const deleted = await deleteRole(roleToDelete.id);
    if (deleted) {
      toast.success("Role deleted");
    } else {
      toast.error(
        useRolesStore.getState().loadingById[roleToDelete.id]?.saveError ??
          "Failed to delete role",
      );
    }
    setRoleToDelete(null);
  };

  return (
    <>
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          {roles.map((role) => (
            <div
              key={role.id}
              className="flex items-center justify-between gap-4 px-4 py-3.5"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium leading-tight">
                    {role.name}
                  </p>
                  {role.isDefault && <Badge variant="secondary">Default</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {role.permissions.length}{" "}
                  {role.permissions.length === 1 ? "permission" : "permissions"}
                </p>
              </div>
              {!role.isDefault && (
                <div className="shrink-0 flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(role)}
                  >
                    <Pencil className="mr-1.5 h-3 w-3" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRoleToDelete(role)}
                    disabled={loadingById[role.id]?.isLoadingDelete ?? false}
                  >
                    {loadingById[role.id]?.isLoadingDelete ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      <AlertDialog
        open={roleToDelete !== null}
        onOpenChange={(open) => !open && setRoleToDelete(null)}
      >
        <AlertDialogContent className="max-w-[calc(100%-2rem)] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {roleToDelete?.name}? Roles that
              are still assigned to users cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <AlertDialogCancel className="mt-0">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Delete Role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
